import { isValidEmail } from '@Utils/helpers/stringHelpers';
import { ITableEditOption } from '@Utils/helpers/tableHelpers';
import { Switch } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import _isNil from 'lodash/isNil';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Button from '@Components/Button/Button';
import CircularLoading from '@Components/CircularLoading/CircularLoading';
import SwitchIIPStyledComponent from '@Components/DomElements/Switch';
import SettingsTable, {
    SettingsTableViewState,
} from '@Components/Settings/components/SettingsTable/SettingsTable';
import Styles from '@Components/Settings/views/SettingsViews.scss';
import { selectedOrganizationState } from '@Data/organizations/Reducer';
import TeamsApi from '@Data/teams/Api';
import { ITeam, IUpdateTeam } from '@Data/teams/Interfaces';
import { teamsState } from '@Data/teams/Reducer';
import SettingsTableButton from '../../../components/SettingsTable/SettingsTableButton';
import { ISettingsBaseComponentProps } from '../../Interfaces';

const emptyUpdateTeam = {
    teamId: '',
    organisationId: '',
    teamName: '',
    ownerEmail: '',
    isActive: true,
};

const OrgTeamsTable = (props: ISettingsBaseComponentProps) => {
    const { title, isSubmitting, setIsSubmitting, id } = props;
    const teams = useSelector(teamsState);
    const selectedOrganization = useSelector(selectedOrganizationState);
    const [selectedTeam, setSelectedTeam] = useState<ITeam>(null);
    const [tableState, setTableState] = useState<SettingsTableViewState>(
        SettingsTableViewState.VIEW
    );
    const [updateTeamEntity, setUpdateTeamEntity] =
        useState<IUpdateTeam>(emptyUpdateTeam);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        getTeams(selectedOrganization.organisation_id);
    }, []);

    const getTeams = async (orgId, teamId?) => {
        setIsLoading(true);
        await TeamsApi.getOrganizationTeams(orgId, teamId);
        setIsLoading(false);
    };

    const columnHelper = createColumnHelper<ITeam>();
    const columns = [
        columnHelper.accessor('owner_email', {
            cell: (info) => info.getValue(),
            header: 'Team Admin IKE ID',
        }),
        columnHelper.accessor('team_name', {
            cell: (info) => info.getValue(),
            header: 'Name',
        }),
        columnHelper.accessor('is_active', {
            cell: (info) => {
                const isActive = info.getValue();
                return SwitchIIPStyledComponent(isActive);
            },
            header: 'Active',
        }),
    ];

    const submitCleanup = async () => {
        setSelectedTeam(null);
        setTableState(SettingsTableViewState.VIEW);
        setUpdateTeamEntity(emptyUpdateTeam);
        setIsSubmitting(false);
    };

    const handleUpdateTeam = async () => {
        setIsSubmitting(true);

        if (tableState === SettingsTableViewState.CREATE) {
            await TeamsApi.createTeam(
                selectedOrganization.organisation_id,
                updateTeamEntity.teamName,
                updateTeamEntity.ownerEmail,
                updateTeamEntity.isActive
            );
            await getTeams(selectedOrganization.organisation_id);
        } else if (tableState === SettingsTableViewState.EDIT) {
            const team = await TeamsApi.updateTeam(
                updateTeamEntity.teamId,
                updateTeamEntity.organisationId,
                updateTeamEntity.teamName,
                updateTeamEntity.ownerEmail,
                updateTeamEntity.isActive
            );
            if (!_isNil(team)) {
                await getTeams(
                    selectedOrganization.organisation_id,
                    team.team_id
                );
            }
        }

        await submitCleanup();
    };

    const handleSetEditState = () => {
        setUpdateTeamEntity({
            teamId: selectedTeam.team_id,
            organisationId: selectedTeam.organisation_fk,
            teamName: selectedTeam.team_name,
            ownerEmail: selectedTeam.owner_email,
            isActive: selectedTeam.is_active,
        });
        setTableState(SettingsTableViewState.EDIT);
    };

    const handleCancelUpdateTeam = () => {
        setTableState(SettingsTableViewState.VIEW);
        setUpdateTeamEntity(emptyUpdateTeam);
    };

    const canCreateNewEntity =
        tableState === SettingsTableViewState.CREATE &&
        updateTeamEntity.teamName !== '' &&
        isValidEmail(updateTeamEntity.ownerEmail);

    const canEditEntity =
        tableState === SettingsTableViewState.EDIT &&
        updateTeamEntity.teamName !== '' &&
        isValidEmail(updateTeamEntity.ownerEmail);

    const editCells: Array<ITableEditOption> = [
        {
            attribute: 'ownerEmail',
            component: <input id="owner-email-input" type="text" />,
        },
        {
            attribute: 'teamName',
            component: <input type="text" id="team-name-input" />,
        },
        { attribute: 'isActive', component: <Switch /> },
    ];

    const createCells: Array<ITableEditOption> = [
        {
            attribute: 'ownerEmail',
            component: <input id="owner-email-input" type="text" />,
        },
        {
            attribute: 'teamName',
            component: <input type="text" id="team-name-input" />,
        },
        { attribute: 'isActive', component: <Switch /> },
    ];

    return (
        <div className={Styles.settingsViewWrapper}>
            <div className={Styles.settingsViewHeader}>
                <span id={`${id}-title`}>{title}</span>

                <div>
                    {tableState !== SettingsTableViewState.VIEW && (
                        <Button
                            id={`cancel-edit-${id}-btn`}
                            color={'black'}
                            onClick={handleCancelUpdateTeam}
                        >
                            Cancel
                        </Button>
                    )}

                    <SettingsTableButton
                        id={`edit-${id}-table-btn`}
                        tableState={tableState}
                        canEdit={!_isNil(selectedTeam)}
                        createText={'Create Team'}
                        editText={'Edit Team'}
                        setCreateState={() =>
                            setTableState(SettingsTableViewState.CREATE)
                        }
                        setEditState={handleSetEditState}
                        onCreateNewEntity={handleUpdateTeam}
                        onEditEntity={handleUpdateTeam}
                        canCreateNewEntity={canCreateNewEntity}
                        canEditEntity={canEditEntity}
                        isSubmitting={isSubmitting}
                    />
                </div>
            </div>
            <div className={Styles.tableContainer}>
                {isLoading ? (
                    <CircularLoading />
                ) : (
                    <SettingsTable
                        id={`${id}-table`}
                        columns={columns}
                        data={teams}
                        onRowClick={(team) => setSelectedTeam(team)}
                        selectedRow={selectedTeam}
                        state={tableState}
                        updateEntity={updateTeamEntity}
                        onChangeUpdateEntity={setUpdateTeamEntity}
                        editCells={editCells}
                        createCells={createCells}
                        isSubmitting={isSubmitting}
                    />
                )}
            </div>
        </div>
    );
};

export default OrgTeamsTable;
